import React, { useEffect, useState } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';

const MEDCALL_PARTICIPANT_INTAKE_OPTIONS = gql`
  query MedcallAdminIntakeOptions {
    medcallParticipantIntakeOptions {
      facilityOptions {
        label
        value
      }
      programOptions {
        label
        requiresClinicalContacts
        requiresFacility
        value
      }
    }
  }
`;

const MEDCALL_SEARCH_DIVERSION_PARTICIPANTS = gql`
  query MedcallAdminSearchDiversionParticipants($programKey: String!, $searchText: String!) {
    medcallSearchDiversionParticipants(programKey: $programKey, searchText: $searchText) {
      cid
      dob
      firstname
      fullname
      in_use
      lastname
      odrparticipant
      participant
      provider
      username
    }
  }
`;

const MEDCALL_FIND_DUPLICATE_PARTICIPANT = gql`
  query MedcallAdminFindDuplicateParticipant($diversionParticipantId: Int!, $programKey: String!) {
    medcallFindDuplicateParticipant(diversionParticipantId: $diversionParticipantId, programKey: $programKey) {
      dob
      firstname
      id
      lastname
      program
    }
  }
`;

const MEDCALL_PARTICIPANT_DETAIL = gql`
  query MedcallAdminParticipantDetail($participantId: Int!) {
    medcallParticipantDetail(participantId: $participantId) {
      id
      firstname
      lastname
      medications {
        active
        frequency
        id
        injection_info {
          administration_notes
        }
        medicine_name
        route
        start_date
        strength
        times_per_day
      }
    }
  }
`;

const MEDCALL_CREATE_PARTICIPANT = gql`
  mutation MedcallAdminCreateParticipant(
    $diversionParticipantId: Int!
    $nurseEmail: String
    $nurseFirstname: String
    $nurseLastname: String
    $nurseTitle: String
    $optionFacilityId: Int
    $programKey: String!
    $psychiatristEmail: String
    $psychiatristFirstname: String
    $psychiatristLastname: String
    $psychiatristTitle: String
  ) {
    medcallCreateParticipant(
      diversionParticipantId: $diversionParticipantId
      nurseEmail: $nurseEmail
      nurseFirstname: $nurseFirstname
      nurseLastname: $nurseLastname
      nurseTitle: $nurseTitle
      optionFacilityId: $optionFacilityId
      programKey: $programKey
      psychiatristEmail: $psychiatristEmail
      psychiatristFirstname: $psychiatristFirstname
      psychiatristLastname: $psychiatristLastname
      psychiatristTitle: $psychiatristTitle
    ) {
      message
      participant {
        firstname
        id
        lastname
      }
      responseCode
    }
  }
`;

const MEDCALL_CREATE_MEDICATION = gql`
  mutation MedcallAdminCreateMedication(
    $active: Boolean
    $administrationNotes: String
    $frequency: String!
    $medicineName: String!
    $participantId: Int!
    $route: String!
    $strength: String!
    $timesPerDay: String!
  ) {
    medcallCreateMedication(
      active: $active
      administrationNotes: $administrationNotes
      frequency: $frequency
      medicineName: $medicineName
      participantId: $participantId
      route: $route
      strength: $strength
      timesPerDay: $timesPerDay
    ) {
      id
    }
  }
`;

const ISSUE_TOKEN = gql`
  mutation MedcallAdminIssueToken($username: String) {
    issueToken(username: $username)
  }
`;

type IntakeOption = {
  label: string;
  value: string;
};

type IntakeProgramOption = IntakeOption & {
  requiresClinicalContacts: boolean;
  requiresFacility: boolean;
};

type SearchParticipant = {
  cid?: string | null;
  dob?: string | null;
  firstname?: string | null;
  fullname?: string | null;
  in_use?: boolean | null;
  lastname?: string | null;
  odrparticipant?: number | null;
  participant: number;
  provider?: string | null;
  username?: string | null;
};

const normalizeText = (value: FormDataEntryValue | null) => `${value || ''}`.trim();

const normalizeOptionalText = (value: FormDataEntryValue | null) => {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) {
    return undefined;
  }

  return normalizedValue;
};

const normalizeOptionalInt = (value: FormDataEntryValue | null) => {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) {
    return undefined;
  }

  const parsedValue = Number(normalizedValue);
  if (!Number.isInteger(parsedValue) || parsedValue < 1) {
    return undefined;
  }

  return parsedValue;
};

export const MedcallAdmin = () => {
  const [activeTab, setActiveTab] = useState<'search' | 'medications' | 'tokens'>('search');
  const [programKey, setProgramKey] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<SearchParticipant[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<SearchParticipant | null>(null);
  const [selectedMedcallParticipantId, setSelectedMedcallParticipantId] = useState<number | null>(null);
  const [selectedDuplicateParticipant, setSelectedDuplicateParticipant] = useState<any>(null);
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showAddMedicationModal, setShowAddMedicationModal] = useState(false);

  const { data: intakeOptionsData } = useQuery(MEDCALL_PARTICIPANT_INTAKE_OPTIONS);
  const programOptions: IntakeProgramOption[] = intakeOptionsData?.medcallParticipantIntakeOptions?.programOptions || [];
  const facilityOptions: IntakeOption[] = intakeOptionsData?.medcallParticipantIntakeOptions?.facilityOptions || [];

  useEffect(() => {
    if (programKey) {
      return;
    }

    if (programOptions.length < 1) {
      return;
    }

    setProgramKey(programOptions[0].value);
  }, [programKey, programOptions]);

  const [searchParticipants, { loading: searchLoading }] = useLazyQuery(MEDCALL_SEARCH_DIVERSION_PARTICIPANTS, {
    onCompleted: (data) => {
      setSearchResults(data?.medcallSearchDiversionParticipants || []);
    },
    onError: (error) => {
      toast.error(`Search error: ${error.message}`);
    },
    fetchPolicy: 'no-cache',
  });

  const [findDuplicateParticipant, { loading: duplicateLoading }] = useLazyQuery(MEDCALL_FIND_DUPLICATE_PARTICIPANT, {
    onCompleted: (data) => {
      const duplicateParticipant = data?.medcallFindDuplicateParticipant || null;
      setSelectedDuplicateParticipant(duplicateParticipant);
      setSelectedMedcallParticipantId(duplicateParticipant?.id ? Number(duplicateParticipant.id) : null);
    },
    onError: (error) => {
      setSelectedDuplicateParticipant(null);
      setSelectedMedcallParticipantId(null);
      toast.error(`Duplicate lookup error: ${error.message}`);
    },
    fetchPolicy: 'no-cache',
  });

  const {
    data: participantDetailData,
    loading: medicationsLoading,
    refetch: refetchParticipantDetail,
  } = useQuery(MEDCALL_PARTICIPANT_DETAIL, {
    variables: { participantId: Number(selectedMedcallParticipantId || 0) },
    skip: !selectedMedcallParticipantId,
  });

  const [createParticipant, { loading: createParticipantLoading }] = useMutation(MEDCALL_CREATE_PARTICIPANT, {
    onCompleted: async (data) => {
      const responseMessage = data?.medcallCreateParticipant?.message || 'Participant added to MedCall';
      toast.success(responseMessage);
      setShowAddParticipantModal(false);

      if (!selectedParticipant) {
        return;
      }

      await findDuplicateParticipant({
        variables: {
          diversionParticipantId: Number(selectedParticipant.participant),
          programKey,
        },
      });
    },
    onError: (error) => {
      toast.error(`Error adding participant: ${error.message}`);
    },
  });

  const [createMedication, { loading: createMedicationLoading }] = useMutation(MEDCALL_CREATE_MEDICATION, {
    onCompleted: async () => {
      toast.success('Medication added successfully');
      setShowAddMedicationModal(false);

      if (!selectedMedcallParticipantId) {
        return;
      }

      await refetchParticipantDetail();
    },
    onError: (error) => {
      toast.error(`Error adding medication: ${error.message}`);
    },
  });

  const [issueToken, { loading: tokenLoading }] = useMutation(ISSUE_TOKEN, {
    onCompleted: (data) => {
      const token = data?.issueToken;
      if (!token) {
        toast.error('Token was not returned');
        return;
      }

      navigator.clipboard.writeText(token);
      toast.success('Token generated and copied to clipboard');
    },
    onError: (error) => {
      toast.error(`Error generating token: ${error.message}`);
    },
  });

  const selectedProgramOption = programOptions.find((option) => option.value === programKey) || null;
  const medications = participantDetailData?.medcallParticipantDetail?.medications || [];

  const handleSearch = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!programKey) {
      toast.error('Program key is required');
      return;
    }

    const normalizedSearchText = searchText.trim();
    if (normalizedSearchText.length < 3) {
      toast.error('Search text must be at least 3 characters');
      return;
    }

    setSelectedParticipant(null);
    setSelectedDuplicateParticipant(null);
    setSelectedMedcallParticipantId(null);
    await searchParticipants({
      variables: {
        programKey,
        searchText: normalizedSearchText,
      },
    });
  };

  const handleSelectParticipant = async (participant: SearchParticipant) => {
    setSelectedParticipant(participant);
    setSelectedDuplicateParticipant(null);
    setSelectedMedcallParticipantId(null);
    await findDuplicateParticipant({
      variables: {
        diversionParticipantId: Number(participant.participant),
        programKey,
      },
    });
  };

  const handleAddParticipant = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const diversionParticipantId = normalizeOptionalInt(formData.get('diversionParticipantId'));
    if (!diversionParticipantId) {
      toast.error('Diversion participant id is required');
      return;
    }

    const submittedProgramKey = normalizeOptionalText(formData.get('programKey')) || programKey;
    if (!submittedProgramKey) {
      toast.error('Program key is required');
      return;
    }

    await createParticipant({
      variables: {
        diversionParticipantId,
        programKey: submittedProgramKey,
        optionFacilityId: normalizeOptionalInt(formData.get('optionFacilityId')),
        nurseEmail: normalizeOptionalText(formData.get('nurseEmail')),
        nurseFirstname: normalizeOptionalText(formData.get('nurseFirstname')),
        nurseLastname: normalizeOptionalText(formData.get('nurseLastname')),
        nurseTitle: normalizeOptionalText(formData.get('nurseTitle')),
        psychiatristEmail: normalizeOptionalText(formData.get('psychiatristEmail')),
        psychiatristFirstname: normalizeOptionalText(formData.get('psychiatristFirstname')),
        psychiatristLastname: normalizeOptionalText(formData.get('psychiatristLastname')),
        psychiatristTitle: normalizeOptionalText(formData.get('psychiatristTitle')),
      },
    });
  };

  const handleAddMedication = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedMedcallParticipantId) {
      toast.error('Select a MedCall participant first');
      return;
    }

    const formData = new FormData(event.currentTarget);
    await createMedication({
      variables: {
        active: formData.get('active') === 'on',
        administrationNotes: normalizeOptionalText(formData.get('administrationNotes')),
        frequency: normalizeText(formData.get('frequency')),
        medicineName: normalizeText(formData.get('medicineName')),
        participantId: selectedMedcallParticipantId,
        route: normalizeText(formData.get('route')),
        strength: normalizeText(formData.get('strength')),
        timesPerDay: normalizeText(formData.get('timesPerDay')),
      },
    });
  };

  const handleGenerateToken = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const username = normalizeOptionalText(formData.get('username'));
    if (!username) {
      toast.error('Username is required');
      return;
    }

    await issueToken({
      variables: {
        username,
      },
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">MedCall Administration</h1>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddParticipantModal(true)}>
            Add Participant
          </Button>
          <Button disabled={!selectedMedcallParticipantId} onClick={() => setShowAddMedicationModal(true)}>
            Add Medication
          </Button>
        </div>
      </div>

      <div className="border-b">
        <nav className="flex space-x-8">
          {[
            { key: 'search', label: 'Participant Search' },
            { key: 'medications', label: 'Medications' },
            { key: 'tokens', label: 'Auth Tokens' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as 'search' | 'medications' | 'tokens')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'search' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Search Diversion Participants</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Program Key</label>
                  <Input
                    list="medcall-program-options"
                    onChange={(event) => setProgramKey(event.target.value)}
                    placeholder="lead, dsh, fist, prg_wmn"
                    value={programKey}
                  />
                  <datalist id="medcall-program-options">
                    {programOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Search Text</label>
                  <Input
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Name, CID, ODR, username"
                    required
                    value={searchText}
                  />
                </div>
                <Button type="submit" disabled={searchLoading}>
                  {searchLoading ? 'Searching...' : 'Search'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Search Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {searchResults.map((participant) => {
                  const isSelected = selectedParticipant?.participant === participant.participant;
                  return (
                    <div
                      key={`${participant.participant}-${participant.odrparticipant || 'none'}`}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                        isSelected ? 'bg-blue-50 border-blue-300' : ''
                      }`}
                      onClick={() => {
                        void handleSelectParticipant(participant);
                      }}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <h3 className="font-medium">
                            {participant.firstname} {participant.lastname}
                          </h3>
                          <p className="text-sm text-gray-600">Diversion Participant: {participant.participant}</p>
                          <p className="text-sm text-gray-600">ODR: {participant.odrparticipant || '—'}</p>
                          <p className="text-sm text-gray-600">DOB: {participant.dob || '—'}</p>
                          <p className="text-sm text-gray-600">Provider: {participant.provider || '—'}</p>
                        </div>
                        <Badge>{participant.in_use ? 'In Use' : 'Available'}</Badge>
                      </div>
                    </div>
                  );
                })}
                {searchResults.length < 1 && (
                  <p className="text-gray-500 text-center">No search results</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Selected Participant Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!selectedParticipant && <p className="text-gray-500">Select a participant to check MedCall status.</p>}
              {selectedParticipant && (
                <>
                  <div className="text-sm text-gray-700">
                    Selected: {selectedParticipant.firstname} {selectedParticipant.lastname} ({selectedParticipant.participant})
                  </div>
                  {duplicateLoading && <p className="text-gray-500">Checking MedCall records...</p>}
                  {!duplicateLoading && !selectedDuplicateParticipant && (
                    <p className="text-gray-500">No MedCall participant record found for this diversion participant.</p>
                  )}
                  {!duplicateLoading && selectedDuplicateParticipant && (
                    <div className="space-y-1">
                      <p className="font-medium">
                        Existing MedCall participant: {selectedDuplicateParticipant.firstname} {selectedDuplicateParticipant.lastname}
                      </p>
                      <p className="text-sm text-gray-600">MedCall ID: {selectedDuplicateParticipant.id}</p>
                      <p className="text-sm text-gray-600">Program: {selectedDuplicateParticipant.program || '—'}</p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'medications' && (
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedMedcallParticipantId
                ? `Medications for ${participantDetailData?.medcallParticipantDetail?.firstname || ''} ${participantDetailData?.medcallParticipantDetail?.lastname || ''}`.trim()
                : 'Select a participant with an existing MedCall record first'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedMedcallParticipantId && (
              <p className="text-gray-500 text-center">Search for a participant, then select one already in MedCall.</p>
            )}
            {selectedMedcallParticipantId && medicationsLoading && <div>Loading medications...</div>}
            {selectedMedcallParticipantId && !medicationsLoading && (
              <div className="space-y-3">
                {medications.map((medication: any) => (
                  <div key={medication.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <h3 className="font-medium">{medication.medicine_name}</h3>
                        <p className="text-sm text-gray-600">
                          Strength: {medication.strength || '—'} | Frequency: {medication.frequency || '—'}
                        </p>
                        <p className="text-sm text-gray-600">
                          Route: {medication.route || '—'} | Times Per Day: {medication.times_per_day || '—'}
                        </p>
                        <p className="text-sm text-gray-600">
                          Start Date: {medication.start_date || '—'}
                        </p>
                        {Array.isArray(medication.injection_info) && medication.injection_info[0]?.administration_notes && (
                          <p className="text-sm text-gray-600 mt-1">
                            Notes: {medication.injection_info[0].administration_notes}
                          </p>
                        )}
                      </div>
                      <Badge variant={medication.active ? 'default' : 'secondary'}>
                        {medication.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                ))}
                {medications.length < 1 && (
                  <p className="text-gray-500 text-center">No medications found</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'tokens' && (
        <Card>
          <CardHeader>
            <CardTitle>Issue Authentication Token</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleGenerateToken} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium mb-2">Username</label>
                <Input name="username" required placeholder="Target username" />
              </div>
              <Button type="submit" disabled={tokenLoading}>
                {tokenLoading ? 'Issuing...' : 'Issue Token'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {showAddParticipantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-medium mb-4">Add Participant to MedCall</h3>
            <form onSubmit={handleAddParticipant} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Diversion Participant ID</label>
                  <Input defaultValue={selectedParticipant?.participant || ''} name="diversionParticipantId" required type="number" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Program Key</label>
                  <Input defaultValue={programKey} list="medcall-program-options" name="programKey" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Facility</label>
                <Input
                  defaultValue=""
                  list="medcall-facility-options"
                  name="optionFacilityId"
                  placeholder={selectedProgramOption?.requiresFacility ? 'Required for this program' : 'Optional'}
                />
                <datalist id="medcall-facility-options">
                  {facilityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </datalist>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nurse First Name</label>
                  <Input name="nurseFirstname" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Nurse Last Name</label>
                  <Input name="nurseLastname" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Nurse Title</label>
                  <Input name="nurseTitle" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Nurse Email</label>
                  <Input name="nurseEmail" type="email" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Psychiatrist First Name</label>
                  <Input name="psychiatristFirstname" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Psychiatrist Last Name</label>
                  <Input name="psychiatristLastname" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Psychiatrist Title</label>
                  <Input name="psychiatristTitle" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Psychiatrist Email</label>
                  <Input name="psychiatristEmail" type="email" />
                </div>
              </div>

              {selectedProgramOption?.requiresClinicalContacts && (
                <p className="text-sm text-amber-700">This program requires nurse contact fields before the mutation will succeed.</p>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowAddParticipantModal(false)}>
                  Cancel
                </Button>
                <Button disabled={createParticipantLoading} type="submit">
                  {createParticipantLoading ? 'Adding...' : 'Add Participant'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddMedicationModal && selectedMedcallParticipantId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl">
            <h3 className="text-lg font-medium mb-4">Add Medication</h3>
            <form onSubmit={handleAddMedication} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Medication Name</label>
                <Input name="medicineName" required />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Strength</label>
                  <Input name="strength" placeholder="10mg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Frequency</label>
                  <Input name="frequency" placeholder="Daily" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Times Per Day</label>
                  <Input name="timesPerDay" placeholder="1" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Route</label>
                <Input name="route" placeholder="PO" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Administration Notes</label>
                <Input name="administrationNotes" />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input defaultChecked name="active" type="checkbox" />
                Active
              </label>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowAddMedicationModal(false)}>
                  Cancel
                </Button>
                <Button disabled={createMedicationLoading} type="submit">
                  {createMedicationLoading ? 'Adding...' : 'Add Medication'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
